import { useCallback, useEffect, useRef, useState } from "react";
import { ErrorState } from "@/components/ErrorState";
import { LoadingState } from "@/components/LoadingState";
import { ReplayControls } from "@/components/ReplayControls";
import { Framebuffer, type Recording, seekStart } from "@/lib/trdp/recording";

export function RdpPlayer({
  recording,
  error,
}: {
  recording: Recording | null;
  error: string | null;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const framebufferRef = useRef<Framebuffer | null>(null);
  const positionRef = useRef(0);
  const nextFrameRef = useRef(0);
  const renderedToRef = useRef(-1);
  const [position, setPosition] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<number>(1);
  const [renderError, setRenderError] = useState<string | null>(null);

  const duration = recording?.durationMs ?? 0;

  const renderAt = useCallback(
    async (targetMs: number) => {
      const framebuffer = framebufferRef.current;

      if (!recording || !framebuffer) return;

      const keyframe = seekStart(recording.frames, targetMs);
      let index = nextFrameRef.current;

      if (targetMs < renderedToRef.current || keyframe > index) {
        index = keyframe;

        if (recording.frames[index]?.kind !== "keyframe") {
          framebuffer.clear(recording.width, recording.height);
        }
      }

      while (index < recording.frames.length) {
        const frame = recording.frames[index];

        if (!frame || frame.elapsedMs > targetMs) break;

        await framebuffer.applyFrame(frame);
        index += 1;
      }

      nextFrameRef.current = index;
      renderedToRef.current = targetMs;
    },
    [recording],
  );

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!recording || !canvas) return;

    const context = canvas.getContext("2d");

    if (!context) {
      setRenderError("This browser can't render the replay.");

      return;
    }

    canvas.width = recording.width;
    canvas.height = recording.height;

    const framebuffer = new Framebuffer(context);
    framebuffer.clear(recording.width, recording.height);
    framebufferRef.current = framebuffer;

    positionRef.current = 0;
    nextFrameRef.current = 0;
    renderedToRef.current = -1;
    setPosition(0);

    void renderAt(0)
      .then(() => {
        setPlaying(true);
      })
      .catch((cause: unknown) => {
        setRenderError(cause instanceof Error ? cause.message : String(cause));
      });
  }, [recording, renderAt]);

  useEffect(() => {
    if (!playing || !recording) return;

    let cancelled = false;
    let frame = 0;
    let last = performance.now();

    const tick = async () => {
      const now = performance.now();
      const next = Math.min(
        duration,
        positionRef.current + (now - last) * speed,
      );
      last = now;

      try {
        await renderAt(next);
      } catch (cause: unknown) {
        if (!cancelled) {
          setRenderError(
            cause instanceof Error ? cause.message : String(cause),
          );
          setPlaying(false);
        }

        return;
      }

      if (cancelled) return;

      positionRef.current = next;
      setPosition(next);

      if (next >= duration) {
        setPlaying(false);

        return;
      }

      frame = requestAnimationFrame(() => void tick());
    };

    frame = requestAnimationFrame(() => void tick());

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [playing, speed, duration, recording, renderAt]);

  const seek = (targetMs: number) => {
    positionRef.current = targetMs;
    setPosition(targetMs);

    void renderAt(targetMs).catch((cause: unknown) => {
      setRenderError(cause instanceof Error ? cause.message : String(cause));
    });
  };

  if (error) return <ErrorState message={error} />;
  if (renderError) return <ErrorState message={renderError} />;
  if (!recording) return <LoadingState />;

  return (
    <div>
      <div className="flex justify-center overflow-hidden rounded border border-muted bg-black">
        <canvas
          ref={canvasRef}
          className="block max-h-[60vh] w-auto max-w-full object-contain"
        />
      </div>

      <ReplayControls
        position={position / 1000}
        duration={duration / 1000}
        playing={playing}
        speed={speed}
        onPlayPause={() => {
          if (!playing && positionRef.current >= duration) seek(0);

          setPlaying(!playing);
        }}
        onSeek={(seconds) => seek(seconds * 1000)}
        onSpeedChange={setSpeed}
      />
    </div>
  );
}
